import * as Popover from "@radix-ui/react-popover";
import { ImageIcon, MagnifyingGlassIcon } from "@radix-ui/react-icons";
import { ImageKind, ImagesService, VariableGroup } from "client/openapi";
import { errorText } from "components/Forms/formsApi";
import { shrink } from "components/PublicBooking/ImageUpload";
import { RefObject, useMemo, useRef, useState } from "react";

type Field = HTMLTextAreaElement | HTMLInputElement;

/** A change to a text box: its new value and what to select after it. */
type Edit = { value: string; start: number; end: number };

/**
 * Put the edit into the box as typing would, so Cmd+Z undoes it in turn
 * with what was typed: the box's own change gives the parent the value, and
 * then the cursor goes where the edit says. Where the browser can't type
 * it, the parent stores the value instead (and undo skips it).
 */
export function applyEdit(
  ref: RefObject<Field>,
  edit: Edit,
  setValue: (value: string) => void
) {
  const field = ref.current;
  if (field && typeInto(field, edit.value)) {
    field.setSelectionRange(edit.start, edit.end);
    return;
  }
  setValue(edit.value);
  requestAnimationFrame(() => {
    const field = ref.current;
    if (!field) return;
    field.focus();
    field.setSelectionRange(edit.start, edit.end);
  });
}

/**
 * Type over only the part of the box that changes, the one way of changing
 * it that the browser's undo remembers. False when the box doesn't end up
 * with the value.
 */
function typeInto(field: Field, value: string) {
  field.focus();
  const old = field.value;
  if (old === value) return true;
  let from = 0;
  while (old[from] === value[from]) from++;
  let back = 0;
  while (
    back < Math.min(old.length, value.length) - from &&
    old[old.length - 1 - back] === value[value.length - 1 - back]
  ) {
    back++;
  }
  const text = value.slice(from, value.length - back);
  field.setSelectionRange(from, old.length - back);
  const typed = text
    ? document.execCommand?.("insertText", false, text)
    : document.execCommand?.("delete");
  return !!typed && field.value === value;
}

/** Text typed in at the cursor (over what is selected). */
export function insertText(field: Field | null, value: string, text: string) {
  const start = field?.selectionStart ?? value.length;
  const end = field?.selectionEnd ?? value.length;
  const next = value.slice(0, start) + text + value.slice(end);
  return { value: next, start: start + text.length, end: start + text.length };
}

export type Format =
  | "heading"
  | "bold"
  | "italic"
  | "bullets"
  | "numbers"
  | "link"
  | "button"
  | "divider";

/** The email formatting the toolbar offers, in the template's own syntax. */
export const FORMATS: { format: Format; label: string; hint: string }[] = [
  { format: "heading", label: "Heading", hint: "# Heading" },
  { format: "bold", label: "Bold", hint: "**bold**" },
  { format: "italic", label: "Italic", hint: "*italic*" },
  { format: "bullets", label: "List", hint: "- item" },
  { format: "numbers", label: "Numbered list", hint: "1. item" },
  { format: "link", label: "Link", hint: "[text](https://…)" },
  { format: "button", label: "Button", hint: "[[Button text|https://…]]" },
  { format: "divider", label: "Divider", hint: "---" },
];

/** Where the line the cursor is on starts. */
const lineStart = (value: string, at: number) =>
  value.lastIndexOf("\n", at - 1) + 1;

/** A block of its own: a blank line before and after it, unless at an end. */
function block(value: string, start: number, end: number, text: string): Edit {
  const before = value.slice(0, start).replace(/\s*$/, "");
  const after = value.slice(end).replace(/^\s*/, "");
  const lead = before ? `${before}\n\n` : "";
  const next = `${lead}${text}${after ? `\n\n${after}` : ""}`;
  return { value: next, start: lead.length, end: lead.length + text.length };
}

/** An uploaded picture on a line of its own at the cursor, by its name. */
export function imageBlock(field: Field | null, value: string, name: string) {
  const start = field?.selectionStart ?? value.length;
  const end = field?.selectionEnd ?? value.length;
  const edit = block(value, start, end, `[Image: ${name}]`);
  // The cursor goes after it, ready for the next paragraph.
  return { ...edit, start: edit.end, end: edit.end };
}

/** The formatting applied at the cursor, around what is selected. */
export function formatted(field: Field | null, value: string, format: Format) {
  const start = field?.selectionStart ?? value.length;
  const end = field?.selectionEnd ?? value.length;
  const selected = value.slice(start, end);

  const wrap = (mark: string, placeholder: string): Edit => {
    const inner = selected || placeholder;
    const next = value.slice(0, start) + mark + inner + mark + value.slice(end);
    const from = start + mark.length;
    return { value: next, start: from, end: from + inner.length };
  };
  const prefix = (mark: (n: number) => string): Edit => {
    // Every selected line starts with the mark (a list's lines numbered).
    const from = lineStart(value, start);
    const lines = value.slice(from, end).split("\n");
    const marked = lines
      .map(
        (line, n) => mark(n) + line.replace(/^(#{1,3}|[-*•]|\d+[.)])\s+/, "")
      )
      .join("\n");
    const next = value.slice(0, from) + marked + value.slice(end);
    return {
      value: next,
      start: from + marked.length,
      end: from + marked.length,
    };
  };

  switch (format) {
    case "bold":
      return wrap("**", "bold text");
    case "italic":
      return wrap("*", "italic text");
    case "heading":
      return prefix(() => "# ");
    case "bullets":
      return prefix(() => "- ");
    case "numbers":
      return prefix((n) => `${n + 1}. `);
    case "link": {
      const text = `[${selected || "link text"}](https://)`;
      const next = value.slice(0, start) + text + value.slice(end);
      // The address is selected, ready to type over.
      const from = start + text.length - "https://)".length;
      return { value: next, start: from, end: from + "https://".length };
    }
    case "button": {
      const edit = block(
        value,
        start,
        end,
        `[[${selected || "Button text"}|https://]]`
      );
      return {
        ...edit,
        start: edit.start + 2,
        end: edit.start + 2 + (selected || "Button text").length,
      };
    }
    default:
      return block(value, start, end, "---");
  }
}

/**
 * Insert a variable: the organization's variables by group, searchable.
 * Picking one types {{key}} at the cursor (onPick).
 */
export function VariablePicker({
  groups,
  onPick,
  disabled,
}: {
  groups: VariableGroup[];
  onPick: (key: string) => void;
  disabled?: boolean;
}) {
  const [open, setOpen] = useState(false);
  const [query, setQuery] = useState("");
  const shown = useMemo(() => {
    const words = query.trim().toLowerCase();
    return groups
      .map((group) => ({
        ...group,
        variables: group.variables.filter(
          (v) =>
            !words ||
            `${group.name} ${v.label} ${v.key}`.toLowerCase().includes(words)
        ),
      }))
      .filter((group) => group.variables.length > 0);
  }, [groups, query]);

  return (
    <Popover.Root
      open={open}
      onOpenChange={(next) => {
        setOpen(next);
        if (!next) setQuery("");
      }}
    >
      <Popover.Trigger asChild>
        <button
          type="button"
          disabled={disabled}
          className="rounded-md border border-[#98acfc] px-2.5 py-1 text-sm font-medium text-[#6b7fe0] hover:bg-[#98acfc]/10 disabled:opacity-50"
        >
          {"{ }"} Insert variable
        </button>
      </Popover.Trigger>
      <Popover.Portal>
        <Popover.Content
          align="start"
          sideOffset={6}
          className="z-[60] w-80 rounded-lg border border-gray-200 bg-white p-2 shadow-lg focus:outline-none"
        >
          <label className="flex items-center gap-2 rounded-md border border-gray-200 px-2 py-1">
            <MagnifyingGlassIcon className="text-gray-400" />
            <input
              autoFocus
              value={query}
              onChange={(e) => setQuery(e.target.value)}
              placeholder="Search variables"
              aria-label="Search variables"
              className="w-full border-0 p-0 text-sm focus:ring-0"
            />
          </label>
          <div className="mt-2 max-h-80 overflow-y-auto">
            {shown.length === 0 && (
              <p className="px-2 py-3 text-sm text-gray-500">
                No variables match.
              </p>
            )}
            {shown.map((group) => (
              <div key={group.name} className="mb-2">
                <p className="px-2 pt-1 text-xs font-semibold uppercase tracking-wide text-gray-500">
                  {group.name}
                </p>
                {group.note && (
                  <p className="px-2 text-xs text-gray-400">{group.note}</p>
                )}
                {group.variables.map((variable) => (
                  <button
                    key={`${group.name}-${variable.key}`}
                    type="button"
                    className="block w-full rounded-md px-2 py-1 text-left text-sm text-gray-800 hover:bg-[#98acfc]/15"
                    onClick={() => {
                      onPick(variable.key);
                      setOpen(false);
                      setQuery("");
                    }}
                  >
                    {variable.label}
                    <span className="block text-xs text-gray-400">
                      {`{{${variable.key}}}`}
                    </span>
                  </button>
                ))}
              </div>
            ))}
          </div>
          <p className="mt-1 border-t border-gray-100 px-2 pt-2 text-xs text-gray-500">
            Add a fallback for when a variable is blank, like{" "}
            {'"Hi {{student.first_name | there}}"'}.
          </p>
        </Popover.Content>
      </Popover.Portal>
    </Popover.Root>
  );
}

/** The email formatting buttons, above the email's body. */
export function FormatBar({
  onFormat,
  disabled,
}: {
  onFormat: (format: Format) => void;
  disabled?: boolean;
}) {
  return (
    <div
      className="flex flex-wrap gap-1"
      role="toolbar"
      aria-label="Formatting"
    >
      {FORMATS.map(({ format, label, hint }) => (
        <button
          key={format}
          type="button"
          title={hint}
          disabled={disabled}
          onClick={() => onFormat(format)}
          className={`rounded-md border border-gray-300 bg-white px-2 py-1 text-xs text-gray-700 hover:bg-gray-50 disabled:opacity-50 ${
            format === "bold"
              ? "font-bold"
              : format === "italic"
              ? "italic"
              : ""
          }`}
        >
          {label}
        </button>
      ))}
    </div>
  );
}

export type EmailFormat = "editor" | "html";

/**
 * Which email is sent: the one written in the editor (in your brand) or
 * your own HTML. Both are kept, so switching loses neither.
 */
export function EmailFormatChoice({
  value,
  onChange,
  disabled,
}: {
  value: EmailFormat;
  onChange: (format: EmailFormat) => void;
  disabled?: boolean;
}) {
  const choice = (format: EmailFormat, label: string) => (
    <button
      type="button"
      aria-pressed={value === format}
      disabled={disabled}
      className={`px-3 py-1 rounded-full text-xs transition-colors duration-200 disabled:opacity-50 ${
        value === format
          ? "bg-[#98acfc] text-white"
          : "text-[#98acfc] hover:bg-[#98acfc]/10"
      }`}
      onClick={() => onChange(format)}
    >
      {label}
    </button>
  );
  return (
    <div
      className="inline-flex rounded-full border border-[#98acfc] p-0.5"
      role="group"
      aria-label="How the email is written"
    >
      {choice("editor", "Editor")}
      {choice("html", "HTML")}
    </div>
  );
}

// Twice a 600-pixel email's width, so pictures stay sharp on phones.
const EMAIL_IMAGE_SIDE = 1200;

/**
 * Upload image: the picture is stored with the app's other images and its
 * address goes to onUploaded, which types it in at the cursor. PNGs stay
 * PNG (for see-through parts), others become JPEG: every mail app shows both.
 */
export function UploadImageButton({
  orgId,
  onUploaded,
  disabled,
}: {
  orgId: number;
  /** Where it is served, and its name for [Image: name]. */
  onUploaded: (url: string, name: string) => void;
  disabled?: boolean;
}) {
  const input = useRef<HTMLInputElement>(null);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string>();

  const upload = async (file?: File) => {
    if (!file) return;
    setBusy(true);
    setError(undefined);
    try {
      const blob = await shrink(
        file,
        EMAIL_IMAGE_SIDE,
        file.type === "image/png" ? ["image/png"] : []
      );
      const { url, name } = await ImagesService.uploadImage({
        formData: {
          kind: ImageKind.EMAIL_IMAGE,
          org_id: orgId,
          file: blob,
          name: file.name,
        },
      });
      onUploaded(url, name ?? file.name);
    } catch (e) {
      setError(
        errorText(e, e instanceof Error ? e.message : "That didn't upload.")
      );
    } finally {
      setBusy(false);
      if (input.current) input.current.value = "";
    }
  };

  return (
    <span className="inline-flex items-center gap-2">
      <input
        ref={input}
        type="file"
        accept="image/png,image/jpeg,image/webp"
        className="hidden"
        onChange={(e) => upload(e.target.files?.[0])}
      />
      <button
        type="button"
        disabled={disabled || busy}
        onClick={() => input.current?.click()}
        className="inline-flex items-center gap-1 rounded-md border border-gray-300 bg-white px-2 py-1 text-xs text-gray-700 hover:bg-gray-50 disabled:opacity-50"
      >
        <ImageIcon /> {busy ? "Uploading…" : "Upload image"}
      </button>
      {error && (
        <span role="alert" className="text-xs text-red-600">
          {error}
        </span>
      )}
    </span>
  );
}

/** The <img> tag Upload image types in: full width on phones, no border. */
export function imageTag(url: string) {
  return `<img src="${url}" alt="" style="display:block;max-width:100%;height:auto;border:0;">`;
}
